/**
 * This is the entry point for Feature Hub App integration
 */

import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { AuthServiceProviderV4 } from '@oneaudi/audi-auth-service';
import type { EnvConfigServiceV1 } from '@oneaudi/audi-env-config-service';
import type { EventServiceV2 as EventService } from '@oneaudi/audi-event-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import type { NotificationDisplayServiceV1 as NotificationDisplayService } from '@oneaudi/audi-notification-display-service';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { AudiHeaderStateServiceInterfaceV2 } from '@oneaudi/header-state-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import I18nContextComponent from '@oneaudi/i18n-context';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { AudiMarketContextServiceV2 } from '@oneaudi/market-context-service';
import type { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import React from 'react';
import FeatureApp from './FeatureApp';
import type { AudiHeaderConfig } from './interfaces/header-components.interfaces';
import type HeaderResponse from './interfaces/header-response.interfaces';
import { fetchHeaderConfig } from './services/navigation-service';
import { cleanNavigationEntries } from './utils/clean-navigation-entries';
import type { ContentHeadless, FalconConfig } from './utils/normalize-falcon-data';
import { normalizeConfig, normalizeContentHeadless } from './utils/normalize-falcon-data';
import { appReady } from './utils/tracking';
import { ServiceContextProvider } from './context/ServiceContext';
import { ConfigContextProvider } from './context/ConfigContext';
import { getDesignLibrary } from './alpha-helper';
import { darkTheme, lightTheme } from './theme';
import { createGlobalStyle } from 'styled-components';
// Type to work with the microkenrel that maybe is available globaly
declare global {
  interface Window {
    microkernel: {
      stateRegistry: {
        subscribeToStore: (
          name: string,
          callback: (state: React.SetStateAction<undefined>) => void,
        ) => void;

        unsubscribeFromStore: (
          name: string,
          callback: (state: React.SetStateAction<undefined>) => void,
        ) => void;
      };
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    SETUPS: {
      get(arg0: string): string;
    };
  }
}

const CustomGlobalStyle = createGlobalStyle`
// Search layer scrolling styles
  .u-header-reset {
    margin: 0;
    padding: 0;
  }

  .u-header-expanded {
    overflow: hidden;
  }

  .u-header-visually-hidden {
    clip: rect(1px, 1px, 1px, 1px);
    height: 1px;
    overflow: hidden;
    position: absolute;
    width: 1px;
  }

  .u-header-nemo-fullwidth-patch {
    display: none;
    height: 72px;
  }

   // set background on DealerSearch container to canvas default
   #dealerSearch {
    padding: 0; margin: 0;
    background: ${({ theme }): string => theme.colors.colorCanvasDefault};
    box-shadow: none;
  } 
`;

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 'audi:envConfigService': EnvConfigServiceV1;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'audi-header-state-service': AudiHeaderStateServiceInterfaceV2;
  readonly 'notification-display-service': NotificationDisplayService;
  readonly 'audi-tracking-service': TrackingServiceV2;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'gfa:dealer-context-service': DealerContextServiceV1;
  readonly 'layer-manager': LayerManagerV25;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'vw:authService': AuthServiceProviderV4;
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 'audi-market-context-service'?: AudiMarketContextServiceV2;
  readonly 's2:logger'?: Logger;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1 | undefined;
  readonly 'event-service'?: EventService;
}

type FeatureHubAppDefinitionType = FeatureAppDefinition<
  ReactFeatureApp,
  FeatureServiceDependencies,
  AudiHeaderConfig | FalconConfig
>;

const featureAppDefinition: FeatureHubAppDefinitionType = {
  create: ({ featureServices, config: rawConfig, featureAppId }) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const config = normalizeConfig(rawConfig);
    const headerConfigDataUrl = config?.navigationServiceUrl;
    const enablePartnerIdReplacement = config?.enablePartnerIdReplacement || false;
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    const asyncSsrManager = featureServices['s2:async-ssr-manager'];
    const contentService = featureServices['audi-content-service'];
    const contentHeadless: ContentHeadless = contentService.getContent();
    // local headless testing
    // const contentHeadless: any = rawConfig;
    const trackingService = featureServices['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = 'fa-nemo-header';
      trackingService.track(appReady);
    }

    let ssrHeaderData: HeaderResponse = {} as HeaderResponse;

    if (contentHeadless?.__type !== 'aem-headless') {
      // async ssr manager only useful in legacy AEM
      if (asyncSsrManager && headerConfigDataUrl) {
        if (typeof serializedStateManager !== 'undefined') {
          serializedStateManager.register(() => JSON.stringify(ssrHeaderData));
        }
        asyncSsrManager.scheduleRerender(
          (async (): Promise<HeaderResponse> => {
            await fetchHeaderConfig(headerConfigDataUrl).then((response) => {
              ssrHeaderData = cleanNavigationEntries(response);
            });
            return ssrHeaderData;
          })(),
        );
      } else {
        const serializedAudiHeaderData = serializedStateManager?.getSerializedState();
        if (serializedAudiHeaderData) {
          ssrHeaderData = JSON.parse(serializedAudiHeaderData);
        }
      }
    } else {
      ssrHeaderData = normalizeContentHeadless(contentHeadless, {
        OneShopEnabled: config?.OneShopEnabled,
        tierOneUrl: config?.tierOneUrl,
      });
    }

    const theme = process.env.IS_LEGACY ? lightTheme : darkTheme;

    return {
      render: (): React.ReactNode => (
        <ThemeProvider theme={theme}>
          <CustomGlobalStyle />
          <ConfigContextProvider config={config} featureAppId={featureAppId}>
            <ServiceContextProvider featureServices={featureServices}>
              <I18nContextComponent
                featureServices={featureServices}
                i18nData={featureServices['dbad:audi-i18n-service']}
                scopes={['nemo.common', 'fa.audi-basket', 'nemo.ui.dealer-context']}
              >
                <FeatureApp
                  enablePartnerIdReplacement={enablePartnerIdReplacement}
                  headerConfigDataUrl={headerConfigDataUrl}
                  ssrHeaderData={ssrHeaderData}
                  contentHeadless={contentHeadless}
                />
              </I18nContextComponent>
            </ServiceContextProvider>
          </ConfigContextProvider>
        </ThemeProvider>
      ),
    };
  },
  dependencies: {
    externals: {
      '@feature-hub/react': '^3.5.0',
      react: '^17.0.2 || ^18.2.0',
      'react-dom': '^17.0.2 || ^18.2.0',
      'styled-components': '^5',
      '@oneaudi/onegraph-client': '*',
      ...getDesignLibrary({
        unifiedWebCommonVersion: '^1.14.0',
      }),
    },
    featureServices: {
      'audi-footnote-reference-service': '3.0.0',
      'audi-header-state-service': '2.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'layer-manager': '2.4.0',
      'locale-service': '^1.0.0',
      'vw:authService': '^4.0.0',
      'audi:envConfigService': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'notification-display-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-market-context-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'gfa:dealer-context-service': '1.0.0',
      'audi-tracking-service': '^2.0.0',
      'event-service': '^2.0.1',
    },
  },
};

export default featureAppDefinition;
